// src/components/explorers/wildfireExplorer/WildfireDetailModal.tsx
import React, { useState } from 'react';
import mapboxgl from 'mapbox-gl';
import { WildfireEntity } from '../../../types/wildfire';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFireFlameCurved, faMapLocationDot } from '@fortawesome/free-solid-svg-icons';
import { Tabs, TabDefinition } from '../../common/Tabs';
import AppModal from '../../common/AppModal';
import { DetailsTab } from './tabs/DetailsTab';
import { ModelsTab } from './tabs/ModelsTab';
import { FilesTab } from './tabs/FilesTab';
import { StatsTab } from './tabs/StatsTab';
import { PerimetersTab } from './tabs/PerimetersTab';
import { LifecycleTab } from './tabs/LifecycleTab';
import { usePermissions } from '../../../context/PermissionContext';

import './WildfireDetailModal.scss';

interface WildfireDetailModalProps {
    wildfire: WildfireEntity;
    onClose: () => void;
    onMinimizeExplorer?: () => void;
    initialTab?: string;
}

export const WildfireDetailModal: React.FC<WildfireDetailModalProps> = ({ wildfire, onClose, onMinimizeExplorer, initialTab = 'details' }) => {
    const data = wildfire.data || wildfire;
    const { canDo } = usePermissions();
    const canViewCms = canDo('cms', 'read');
    const [activeTab, setActiveTab] = useState<string>(initialTab);

    // Files tab content is sourced from CMS — hide the whole tab if the
    // user lacks cms.read so we don't show empty/forbidden UI.
    const tabs: TabDefinition[] = [
        { id: 'details', label: 'Details', content: <DetailsTab wildfire={wildfire} /> },
        { id: 'models', label: 'Models', content: <ModelsTab wildfire={wildfire} /> },
        ...(canViewCms ? [{ id: 'files', label: 'Files', content: <FilesTab wildfire={wildfire} /> }] : []),
        { id: 'stats', label: 'Stats', content: <StatsTab wildfire={wildfire} /> },
        { id: 'perimeters', label: 'Perimeters', content: <PerimetersTab fireNumber={wildfire.fire_number} /> },
        { id: 'lifecycle', label: 'Lifecycle', content: <LifecycleTab wildfire={wildfire} /> },
    ];

    const handleShowOnMap = () => {
        const lat = data.latitudeDD;
        const lng = data.longitudeDD;
        if (!(lat && lng && window.map)) return;

        onClose();
        onMinimizeExplorer?.();
        window.map.flyTo({ center: [lng, lat], zoom: 12 });
        window.map.once('moveend', () => {
            document.querySelectorAll('.wildfire-detail-popup').forEach((el) => el.closest('.mapboxgl-popup')?.remove());
            new mapboxgl.Popup({ closeButton: true, closeOnClick: true, className: 'wildfire-detail-popup', maxWidth: '280px', anchor: 'bottom', offset: 30 })
                .setLngLat([lng, lat])
                .setHTML(`
                    <div style="padding:8px;background:#fff;border-radius:6px;color:#333;font-size:13px;line-height:1.5">
                        <div style="font-weight:700;font-size:14px;margin-bottom:4px">${wildfire.fire_number}</div>
                        <div><b>Status:</b> ${wildfire.wildfire_status || '—'}</div>
                        <div><b>Size:</b> ${wildfire.estimated_size ? wildfire.estimated_size + ' ha' : '—'}</div>
                        <div><b>Region:</b> ${wildfire.region || '—'}</div>
                        <div style="margin-top:4px;font-size:11px;color:#888">${Number(lat).toFixed(5)}, ${Number(lng).toFixed(5)}</div>
                    </div>
                `)
                .addTo(window.map!);
        });
    };

    return (
        <AppModal
            isOpen={true}
            onClose={onClose}
            width="min(800px, 90vw)"
            maxHeight="80vh"
            minHeight="80vh"
            header={
                <span className="wildfire-detail-header__title">
                    <FontAwesomeIcon icon={faFireFlameCurved} /> Wildfire: {data.number || wildfire.fire_number}
                </span>
            }
            headerAccent
        >
            <div className="wildfire-detail-toolbar">
                <button
                    className="btn btn-sm btn-outline"
                    title="Show on map"
                    onClick={handleShowOnMap}
                    /* On Perimeters the per-perimeter Show on Map action is
                       the right primary — keep the slot so tabs don't shift
                       upward, but hide the button to prevent users from
                       clicking it and getting the fire-popup instead. */
                    style={activeTab === 'perimeters' ? { visibility: 'hidden' } : undefined}
                    aria-hidden={activeTab === 'perimeters'}
                    tabIndex={activeTab === 'perimeters' ? -1 : 0}
                >
                    <FontAwesomeIcon icon={faMapLocationDot} /> Map
                </button>
            </div>

            <Tabs tabs={tabs} defaultTab={initialTab} onTabChange={setActiveTab} />
        </AppModal>
    );
};
